import { createElement, useState } from "react";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { Trans } from "react-i18next";
import { useTranslation } from "@localization/useTranslation";
import { post } from "@common/api/apiShared";
import { Result } from "@common/api/result";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { useValidator } from "@common/hooks/validation";
import { getEnumValues } from "@common/enumOperations";
import { useRiskAreas } from "@common/hooks/useSelectableItemQueries";
import { getEntityTypeOptions } from "../../../riskSettings/riskScenarios/RiskScenarios.hooks";
import { allOptionId, resolveSelectionWithAll } from "../../../riskSettings/common/RiskSettings.utils";
import { RiskScenarioCategory } from "../../../riskSettings/riskScenarios/riskScenarioDialog/RiskScenarioDialog.types";
import { IConvertObservationDialog } from "./ConvertObservationDialog";
import { ConvertObservationForm } from "./ConvertObservationDialog.types";

export function useConvertObservationDialogDataMapping(props: IConvertObservationDialog) {
    const snackbar = useDotLegalSnackbar();
    const { translateString, translateRiskScenarioCategory, translateEntityType } = useTranslation();
    const { isLoading: riskAreasLoading, riskAreas } = useRiskAreas();

    const [form, setForm] = useState<ConvertObservationForm>({
        name: props.observation.name,
        description: props.observation.description,
        category: "",
        riskAreaIds: [],
        entityTypes: [],
        notifyResponsibles: false,
    });
    const [chosenRiskAreas, setChosenRiskAreas] = useState<string[]>([allOptionId]);
    const [chosenEntityTypes, setChosenEntityTypes] = useState<string[]>([allOptionId]);

    const validation = useValidator<{ name: string; category: string; riskAreas: string[]; entityTypes: string[] }>((validators) => ({
        name: validators.validateNotEmpty((x) => x.name, translateString("riskScenario")),
        category: validators.validateNotEmpty((x) => x.category, translateString("category")),
        riskAreas: validators.validateNotEmpty((x) => x.riskAreas, translateString("riskAreas")),
        entityTypes: validators.validateNotEmpty((x) => x.entityTypes, translateString("types")),
    }));

    validation.check({ name: form.name, category: form.category, riskAreas: chosenRiskAreas, entityTypes: chosenEntityTypes });

    const categoryOptions = getEnumValues(RiskScenarioCategory).map((category) => ({ id: category.toString(), name: translateRiskScenarioCategory(category) }));

    const riskAreaOptions = [{ id: allOptionId, name: translateString("allRiskAreas") }, ...(riskAreas ?? [])];

    const entityTypeOptions = [{ id: allOptionId, name: translateString("allTypes") }, ...getEntityTypeOptions(translateEntityType)];

    const convertMutation = useDotLegalMutation(convert, {
        onSuccess: (response: Result<string>) => {
            if (response.success()) {
                props.onConverted();
                props.onCloseDialog();
                snackbar.show(createElement(Trans, { i18nKey: "observationConverted", values: { name: form.name } }));
            }
        },
    });

    function convert() {
        const body = {
            name: form.name,
            description: form.description,
            category: Number(form.category),
            riskAreaIds: form.riskAreaIds,
            entityTypes: form.entityTypes,
            notifyResponsibles: form.notifyResponsibles,
        };
        return post<string>(`/observations/${props.observation.id}/convert`, body);
    }

    const setName = (name: string) => setForm({ ...form, name });

    const setDescription = (description: string) => setForm({ ...form, description });

    const setCategory = (category: string | null) => setForm({ ...form, category: category ?? "" });

    const setRiskAreas = (values: string[]) => {
        const selection = resolveSelectionWithAll(values, chosenRiskAreas);
        setChosenRiskAreas(selection);
        setForm({ ...form, riskAreaIds: selection.includes(allOptionId) ? [] : selection });
    };

    const setEntityTypes = (values: string[]) => {
        const selection = resolveSelectionWithAll(values, chosenEntityTypes);
        setChosenEntityTypes(selection);
        setForm({ ...form, entityTypes: selection.includes(allOptionId) ? [] : selection.map(Number) });
    };

    const setNotifyResponsibles = (notifyResponsibles: boolean) => setForm({ ...form, notifyResponsibles });

    const validateAndConvert = () => {
        if (validation.anyHasErrors) {
            validation.setShowErrors(true);
            return;
        }

        convertMutation.mutateAsync();
    };

    return {
        form,
        validation,
        inProgress: convertMutation.isLoading,
        setName,
        setDescription,
        setCategory,
        setRiskAreas,
        chosenRiskAreas,
        setEntityTypes,
        chosenEntityTypes,
        setNotifyResponsibles,
        categoryOptions,
        riskAreaOptions,
        riskAreasLoading,
        entityTypeOptions,
        validateAndConvert,
    };
}
